/**
 * Lightweight client-side heuristic that flags terms in a user's content/video
 * topic which OpenAI's Sora 2 moderation reliably rejects. Use ONLY as a UX
 * pre-flight nudge — the authoritative ruling still happens server-side.
 *
 * Tiers:
 *  • brands       — global media/tech/consumer brands (~80% of rejections)
 *  • people       — public figures Sora 2 will not depict
 *  • sensitive    — themes that trigger automatic refusal even when phrased neutrally
 *
 * Detection is case-insensitive word-boundary matching, so "iphone" inside
 * "epiphone" (a guitar brand which is also blocked, but ignore for now) won't
 * trigger on the substring.
 */
const TIER = (terms, replacement) => terms.map((t) => ({ term: t, replacement }));

export const SORA2_BLOCKED_TERMS = [
  // --- Media / broadcasters ---
  ...TIER(["NPR", "BBC", "CNN", "Fox News", "MSNBC", "NBC", "ABC", "CBS", "Sky News", "Al Jazeera", "Reuters", "Bloomberg", "The Guardian", "The Times", "Wall Street Journal", "WSJ", "Financial Times", "FT", "New York Times", "NYT", "The Verge", "TechCrunch"], "a major media outlet"),
  // --- Tech giants ---
  ...TIER(["Google", "Apple", "Microsoft", "Amazon", "Meta", "Facebook", "Instagram", "WhatsApp", "TikTok", "Twitter", "X.com", "Threads", "Snapchat", "Pinterest", "LinkedIn", "Netflix", "Spotify", "Disney", "Disney+", "YouTube", "OpenAI", "Anthropic", "Nvidia", "Tesla", "SpaceX", "Uber", "Airbnb", "Stripe", "PayPal", "Reddit", "Discord", "Telegram"], "a large tech platform"),
  // --- Streaming / podcasts ---
  ...TIER(["Joe Rogan Experience", "JRE", "Spotify Originals", "Apple Podcasts", "Audible", "iHeart", "Wondery", "Pushkin"], "a popular podcast network"),
  // --- Consumer / fashion / sport ---
  ...TIER(["Nike", "Adidas", "Puma", "Lululemon", "Gucci", "Louis Vuitton", "Chanel", "Rolex", "Ferrari", "Lamborghini", "BMW", "Mercedes-Benz", "Audi", "Porsche", "McDonald's", "Starbucks", "Coca-Cola", "Pepsi", "Red Bull"], "a well-known consumer brand"),
  // --- Public figures (representative; not exhaustive) ---
  ...TIER(["Elon Musk", "Mark Zuckerberg", "Sam Altman", "Bill Gates", "Steve Jobs", "Jeff Bezos", "Tim Cook", "Sundar Pichai", "Joe Biden", "Donald Trump", "Kamala Harris", "Barack Obama", "King Charles", "Taylor Swift", "Beyoncé", "Drake", "Kanye West", "Kim Kardashian", "Cristiano Ronaldo", "Lionel Messi", "LeBron James", "Tom Cruise", "Joe Rogan", "Oprah Winfrey", "MrBeast"], "a well-known public figure"),
  // --- Sensitive / hard refusals ---
  ...TIER(["violence", "blood", "weapon", "gun", "firearm", "shooting", "kill", "suicide", "self-harm", "nudity", "explicit", "porn", "minor", "child", "abuse", "drugs", "cocaine", "heroin", "fentanyl"], "a more neutral topic phrasing"),
];

/**
 * Scans a topic string and returns the matched blocked terms with their suggested
 * generic replacement. Returns [] if topic is empty or no matches.
 *
 *   detectBlockedTerms("How NPR cut costs with AI")
 *     → [{ term: "NPR", replacement: "a major media outlet" }]
 */
export function detectBlockedTerms(topic) {
  if (!topic || typeof topic !== "string" || topic.trim().length < 2) return [];
  const lower = topic.toLowerCase();
  const seen = new Set();
  const hits = [];
  for (const { term, replacement } of SORA2_BLOCKED_TERMS) {
    const re = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
    if (re.test(topic) && !seen.has(term.toLowerCase())) {
      seen.add(term.toLowerCase());
      hits.push({ term, replacement });
    }
    // Cheap second-pass for terms that lose word boundaries (e.g. "X.com")
    if (term.includes(".") && lower.includes(term.toLowerCase()) && !seen.has(term.toLowerCase())) {
      seen.add(term.toLowerCase());
      hits.push({ term, replacement });
    }
  }
  return hits;
}

/**
 * Applies the suggested replacements to a topic, producing a Sora-2-friendly
 * rewrite the user can adopt in one click.
 *
 *   suggestRewrite("How NPR cut costs with AI")
 *     → "How a major media outlet cut costs with AI"
 */
export function suggestRewrite(topic, hits) {
  if (!topic || !hits || hits.length === 0) return topic;
  let out = topic;
  for (const { term, replacement } of hits) {
    const re = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi");
    out = out.replace(re, replacement);
  }
  // Collapse "a/an" article duplicates we may have introduced ("a a major" → "a major")
  out = out.replace(/\b(a|an)\s+(a|an)\s+/gi, "$1 ");
  return out;
}
